import { inject, Injectable } from '@angular/core';
import { catchError, first, forkJoin, map, Observable, of, tap } from 'rxjs';
import { Location } from '../../domain/models/location.model';
import { Drawer } from '@features/service/domain/models/drawer.model';
import { Worker } from '@features/service/domain/models/worker.model';
import { SERVICE_REPOSITORY } from '../../domain/tokens/service.token';
import { SERVICE_STORE } from '../../domain/tokens/service-store.token';
import { ServiceRelations } from '@features/service/domain/models/service-relations.model';
import { EVENT_BUS } from '@shared/domain/tokens/event-bus.token';
import { ServiceSelectionChangedEvent } from '@platform/event-bus/domain/events/service-selection-change.event';
import { DEVICE } from '@shared/domain/tokens/device.token';

@Injectable({ providedIn: 'root' })
export class FetchServiceUseCase {
  private readonly serviceRepository = inject(SERVICE_REPOSITORY);
  private readonly serviceStore = inject(SERVICE_STORE);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly devicePort = inject(DEVICE);

  execute(): Observable<void> {
    const currentState = this.serviceStore.state();
    const preservedSelection = currentState.selection;

    return forkJoin({
      locations: this.getLocations(),
      drawers: this.getDrawers(),
      workers: this.getWorkers(),
      relations: this.getServiceRelations(),
      device: this.devicePort.getDeviceInfoAsync().pipe(
        catchError(() => of(null))
      ),
    }).pipe(
      first(),
      tap(({ locations, drawers, workers, relations, device }) => {
        this.serviceStore.setLocations(locations);
        this.serviceStore.setDrawers(drawers);
        this.serviceStore.setWorkers(workers);
        this.serviceStore.setServiceRelations(relations);

        if (device) {
          this.serviceStore.setIsWorkersOwnedDevice(device.isPersonal);
        }

        if (preservedSelection) {
          const restoredSelection: typeof preservedSelection = {
            location: null,
            drawer: null,
            worker: null,
          };

          if (preservedSelection.location) {
            const locationExists = locations.find(l => l.id === preservedSelection.location!.id);

            if (locationExists) {
              restoredSelection.location = locationExists;
            }
          }

          if (preservedSelection.drawer) {
            const drawerExists = drawers.find(d => d.id === preservedSelection.drawer!.id);

            if (drawerExists) {
              restoredSelection.drawer = drawerExists;
            }
          }

          if (preservedSelection.worker) {
            const workerExists = workers.find(w => w.id === preservedSelection.worker!.id);

            if (workerExists) {
              restoredSelection.worker = workerExists;
            }
          }

          if (restoredSelection.location || restoredSelection.drawer || restoredSelection.worker) {
            this.serviceStore.setSelection(restoredSelection);
          }
        }
      }),
      tap(() => {
        this.eventBus.publish(new ServiceSelectionChangedEvent(this.serviceStore.state().selection));
      }),
      map(() => void 0)
    );
  }

  getLocations(): Observable<Location[]> {
    return this.serviceRepository.getLocations().pipe(
      catchError(() => {
        return of([]);
      })
    );
  }

  getDrawers(): Observable<Drawer[]> {
    return this.serviceRepository.getDrawers().pipe(
      catchError(() => {
        return of([]);
      })
    );
  }

  getWorkers(): Observable<Worker[]> {
    return this.serviceRepository.getWorkers().pipe(
      catchError(() => {
        return of([]);
      })
    );
  }

  getServiceRelations(): Observable<ServiceRelations> {
    return this.serviceRepository.getServiceRelations().pipe(
      catchError(() => {
        return of({
          locationsToCashDrawers: {},
          cashDrawersToWorkers: {},
          workersToLocations: {},
        } as ServiceRelations);
      })
    );
  }
}
